import { useState, useEffect } from "react";

const SplashScreen = ({ onComplete }) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [showTagline, setShowTagline] = useState(false);
  const [progress, setProgress] = useState(0);
  const [skipSplash, setSkipSplash] = useState(false);

  const SPLASH_DURATION = 5000; // 5 seconds

  useEffect(() => {
    // Check for force_splash query parameter (for testing)
    const urlParams = new URLSearchParams(window.location.search);
    const forceSplash = urlParams.get('force_splash') === 'true';
    
    // Check if user has visited before (skip check if force_splash is set)
    const hasVisited = localStorage.getItem('karine_splash_seen');
    if (hasVisited && !forceSplash) {
      setSkipSplash(true);
      onComplete();
      return;
    }

    // Mark as visited for future
    localStorage.setItem('karine_splash_seen', 'true');

    // Small delay to let the page settle before showing splash
    const readyTimer = setTimeout(() => {
      setIsReady(true);
    }, 100);

    // Show tagline after 2.5 seconds
    const taglineTimer = setTimeout(() => {
      setShowTagline(true);
    }, 2500);

    // Progress bar animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) return 100;
        return prev + (100 / (SPLASH_DURATION / 50));
      });
    }, 50);

    // Start fade out after video plays
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, SPLASH_DURATION);

    // Complete and remove splash after fade animation
    const completeTimer = setTimeout(() => {
      onComplete();
    }, SPLASH_DURATION + 2500);

    return () => {
      clearTimeout(readyTimer);
      clearTimeout(taglineTimer);
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
      clearInterval(progressInterval);
    };
  }, [onComplete]);

  const handleSkip = () => {
    setFadeOut(true);
    setTimeout(() => {
      onComplete();
    }, 2000);
  };

  // Don't render anything if returning visitor
  if (skipSplash) return null;

  return (
    <div
      className={`fixed inset-0 z-[99999] flex flex-col items-center justify-center ${
        fadeOut ? "pointer-events-none" : ""
      }`}
      data-testid="splash-screen"
    >
      {/* Overlay with blur - starts at 75% dark with blur, fades to transparent and clear */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundColor: fadeOut ? 'rgba(0,0,0,0)' : 'rgba(0,0,0,0.75)',
          backdropFilter: fadeOut ? 'blur(0px)' : 'blur(8px)',
          WebkitBackdropFilter: fadeOut ? 'blur(0px)' : 'blur(8px)',
          transition: isReady ? 'background-color 2s cubic-bezier(0.4, 0, 0.2, 1), backdrop-filter 2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
        }}
      />
      
      {/* Logo container with glow effect */}
      <div 
        className="relative z-10"
        style={{
          filter: fadeOut ? 'drop-shadow(0 0 0px rgba(212, 175, 55, 0))' : 'drop-shadow(0 0 30px rgba(212, 175, 55, 0.5)) drop-shadow(0 0 60px rgba(212, 175, 55, 0.3))',
          transition: isReady ? 'filter 2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
        }}
      >
        <video
          src="https://customer-assets.emergentagent.com/job_85a700af-9f96-42a1-b28b-49447854ad83/artifacts/8e9x4nb7_logo%20animated.MP4"
          autoPlay
          muted
          playsInline
          className="w-full max-w-2xl h-auto"
          style={{
            opacity: fadeOut ? 0 : (isReady ? 1 : 0),
            transform: fadeOut ? 'scale(0.95)' : 'scale(1)',
            transition: isReady ? 'opacity 2s cubic-bezier(0.4, 0, 0.2, 1), transform 2s cubic-bezier(0.4, 0, 0.2, 1)' : 'opacity 0.3s ease'
          }}
        />
      </div>

      {/* Tagline fade-in */}
      <div 
        className="relative z-10 mt-6 text-center"
        style={{
          opacity: fadeOut ? 0 : (showTagline ? 1 : 0),
          transform: fadeOut ? 'translateY(-10px)' : (showTagline ? 'translateY(0)' : 'translateY(10px)'),
          transition: 'opacity 1s cubic-bezier(0.4, 0, 0.2, 1), transform 1s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <p 
          className="text-white/90 text-xl md:text-2xl font-light tracking-wide"
          style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif" }}
        >
          Precision. <span className="italic text-amber-300">Expertise.</span> Natural Results.
        </p>
      </div>

      {/* Progress bar */}
      <div 
        className="relative z-10 w-full max-w-md mt-8 px-8"
        style={{
          opacity: fadeOut ? 0 : (isReady ? 1 : 0),
          transition: 'opacity 1s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <div className="h-0.5 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-amber-400 to-amber-200 rounded-full"
            style={{
              width: `${progress}%`,
              transition: 'width 0.05s linear'
            }}
          />
        </div>
      </div>
      
      {/* Skip Button - below progress bar, right aligned */}
      <div 
        className="relative z-10 w-full max-w-md flex justify-end mt-4 px-8"
        style={{
          opacity: fadeOut ? 0 : (isReady ? 1 : 0),
          transition: isReady ? 'opacity 1s cubic-bezier(0.4, 0, 0.2, 1)' : 'opacity 0.3s ease'
        }}
      >
        <button
          onClick={handleSkip}
          className="text-white/60 hover:text-white text-sm font-medium tracking-wider uppercase flex items-center gap-2 group px-4 py-2 rounded-full hover:bg-white/10"
          style={{
            transition: 'all 0.3s ease'
          }}
          data-testid="skip-splash-btn"
        >
          Skip
          <svg 
            className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SplashScreen;
